// ─── Symbol pip metadata (broker pipPosition) ────────────────────
// The bot's pip size MUST follow the broker's authoritative `pipPosition`
// (ProtoOASymbol.pipPosition), not a name-based guess. Old symbol lists carry
// `pipPosition: null` (the field wasn't captured when they were created), which
// made every engine fall back to a heuristic (JPY → 0.01, XAU/XAG → 0.01,
// otherwise 0.0001). Any broker whose real pip convention differs from that
// guess then reports wrong pip distances (trend candles, spreads, thresholds).
//
// This helper fetches the broker's real symbol metadata (pipPosition + digits)
// through the ctrader-gateway and patches every entry whose pipPosition is
// missing. Results are cached in-memory per account so repeated engine inits
// within one session never re-hit the broker.
//
// After a successful fetch the resolved values are ALSO written back into the
// `symbol_lists` rows of that account, so the field is filled ONCE and later
// sessions read it straight from the database (no broker call at all).

import { supabase } from '@/lib/supabase';

export interface SymbolPipMeta {
  symbolId: number;
  symbolName: string;
  digits?: number | null;
  pipPosition?: number | null;
}

interface BrokerSymbolMeta {
  pipPosition: number | null;
  digits: number | null;
}

/** accountId → (symbolId → broker meta). Session cache. */
const brokerMetaCache = new Map<string, Map<number, BrokerSymbolMeta>>();

/** Accounts whose resolved pip metadata was already persisted this session. */
const persistedAccounts = new Set<string>();

async function fetchBrokerSymbolMeta(accountId: string): Promise<Map<number, BrokerSymbolMeta>> {
  const cached = brokerMetaCache.get(accountId);
  if (cached) return cached;

  const meta = new Map<number, BrokerSymbolMeta>();
  const numId = parseInt(accountId, 10);
  if (isNaN(numId)) return meta;

  try {
    const { data } = await supabase.functions.invoke<{
      success: boolean;
      symbolsRes: Record<string, unknown> | null;
    }>('ctrader-gateway', {
      method: 'POST',
      body: { action: 'get-symbols-list', accountId: numId, includeSymbolsWithoutTickets: true },
    });

    if (data?.success && data.symbolsRes) {
      const arr = Array.isArray(data.symbolsRes.symbol)
        ? (data.symbolsRes.symbol as Array<Record<string, unknown>>)
        : [];
      for (const s of arr) {
        const id = typeof s.symbolId === 'number'
          ? s.symbolId
          : typeof s.symbolId === 'string'
            ? parseInt(s.symbolId, 10)
            : NaN;
        if (!Number.isFinite(id) || id <= 0) continue;
        meta.set(id, {
          pipPosition: typeof s.pipPosition === 'number' ? s.pipPosition : null,
          digits: typeof s.digits === 'number' ? s.digits : null,
        });
      }
    }
  } catch {
    return meta; // never cache a failure — allow a retry on the next init
  }

  if (meta.size > 0) brokerMetaCache.set(accountId, meta);
  return meta;
}

/**
 * Best-effort write-back of the resolved broker metadata into every
 * `symbol_lists` row of this account whose entries still have a blank
 * `pipPosition`/`digits`. This fills the field ONCE, so future sessions read
 * the real value straight from the database and never call the broker again.
 *
 * Runs once per account per session. Never throws — a failed write must never
 * break engine init (the in-session cache still keeps things correct).
 */
async function persistResolvedPipPositions(
  accountId: string,
  meta: Map<number, BrokerSymbolMeta>,
): Promise<void> {
  if (meta.size === 0) return;
  if (persistedAccounts.has(accountId)) return;
  persistedAccounts.add(accountId);

  try {
    const { data, error } = await supabase
      .from('symbol_lists')
      .select('id, symbols')
      .eq('ctrader_account_id', accountId);

    if (error || !data) return;

    for (const row of data as Array<{ id: string; symbols: unknown }>) {
      const arr = Array.isArray(row.symbols)
        ? (row.symbols as Array<Record<string, unknown>>)
        : [];
      if (arr.length === 0) continue;

      let changed = false;
      const next = arr.map((s) => {
        const id = typeof s.symbolId === 'number' ? s.symbolId : parseInt(String(s.symbolId), 10);
        const m = Number.isFinite(id) ? meta.get(id) : undefined;
        if (!m) return s;
        const copy = { ...s };
        if ((copy.pipPosition === null || copy.pipPosition === undefined) && m.pipPosition !== null) {
          copy.pipPosition = m.pipPosition;
          changed = true;
        }
        if ((copy.digits === null || copy.digits === undefined) && m.digits !== null) {
          copy.digits = m.digits;
          changed = true;
        }
        return copy;
      });

      if (!changed) continue;
      await supabase
        .from('symbol_lists')
        .update({ symbols: next, updated_at: new Date().toISOString() })
        .eq('id', row.id);
    }
  } catch {
    // Allow a retry on the next init instead of silently giving up forever.
    persistedAccounts.delete(accountId);
  }
}

/**
 * Patch any symbol entry whose `pipPosition` (or `digits`) is missing with the
 * broker's authoritative value. Returns a NEW array; the input is untouched.
 * When every entry already has a pipPosition, the input is returned unchanged
 * (no broker call). When the broker is unreachable, the input is returned too,
 * so the caller keeps its existing (heuristic) fallback.
 */
export async function hydrateSymbolPipPositions(
  accountId: string,
  symbols: SymbolPipMeta[],
): Promise<SymbolPipMeta[]> {
  if (symbols.length === 0) return symbols;
  const needs = symbols.some((s) => s.pipPosition === null || s.pipPosition === undefined);
  if (!needs) return symbols;

  const meta = await fetchBrokerSymbolMeta(accountId);
  if (meta.size === 0) return symbols;

  // Fill the database ONCE so later sessions skip the broker entirely.
  void persistResolvedPipPositions(accountId, meta);

  return symbols.map((sym) => {
    const m = meta.get(sym.symbolId);
    if (!m) return sym;
    const next: SymbolPipMeta = { ...sym };
    if ((next.pipPosition === null || next.pipPosition === undefined) && m.pipPosition !== null) {
      next.pipPosition = m.pipPosition;
    }
    if ((next.digits === null || next.digits === undefined) && m.digits !== null) {
      next.digits = m.digits;
    }
    return next;
  });
}